import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { css, keyframes } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { NakedButton, PopoverDialog, SkeletonLoader, usePopoverDialog, useTooltip } from '@frontend/design-system';
import { SmartDraftMobileLoader } from './smart-draft-mobile-loader';
import { SmartDraftResultBar } from './smart-draft-result-bar';
import {
  MOBILE_POPOVER_BREAKPOINT,
  INLINE_WIDTH,
  INLINE_WIDTH_MEDIUM,
  INLINE_WIDTH_SMALL,
  POPOVER_WIDTH,
  POPOVER_HEIGHT,
  COMPACT_BUTTON_WIDTH,
  COMPACT_BUTTON_HEIGHT,
  expandBar,
} from './smart-draft.constants';
import type { SmartDraftButtonProps } from './smart-draft.types';

// ── Button styles ──────────────────────────────────────────────────
const baseButtonStyles = css({
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(0.5),
  cursor: 'pointer',
  minHeight: 32,
  minWidth: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 200ms ease',
});

const hoverStyles = css({
  '&:hover:not(:disabled)': {
    backgroundColor: theme.colors.neutral5,
  },
});

const compactBaseStyles = css({
  height: COMPACT_BUTTON_HEIGHT,
  minHeight: COMPACT_BUTTON_HEIGHT,
  width: COMPACT_BUTTON_WIDTH,
  minWidth: COMPACT_BUTTON_WIDTH,
  padding: 'var(--fabric-number-n-2-5)',
  borderRadius: COMPACT_BUTTON_HEIGHT / 2,
  // box-shadow instead of border so it can be animated to transparent on dismiss
  boxShadow: `inset 0 0 0 0.5px ${theme.colors.neutral30}`,
  backgroundColor: theme.colors.white,
  '&:hover:not(:disabled)': {
    backgroundColor: theme.colors.neutral5,
  },
});

// Plays when the pill button collapses back to the plain borderless icon button after dismiss.
// The `from` values replicate the pill's visual state so the animation starts from there
// even though compactBaseStyles has already been removed from the element.
const pillCollapse = keyframes`
  from {
    width: ${COMPACT_BUTTON_WIDTH}px;
    padding: ${theme.spacing(0, 1)};
    border-radius: ${COMPACT_BUTTON_HEIGHT / 2}px;
    box-shadow: inset 0 0 0 0.5px ${theme.colors.neutral30};
    background-color: ${theme.colors.white};
  }
  to {
    width: 32px;
    padding: 0;
    box-shadow: inset 0 0 0 0px transparent;
    background-color: transparent;
  }
`;

const disabledStyles = css({
  cursor: 'not-allowed',
  opacity: 0.5,
});

const inlineLoadingStyles = css({
  borderRadius: theme.borderRadius.large,
  backgroundColor: theme.colors.white,
  padding: theme.spacing(0, 0.5),
  width: INLINE_WIDTH,
  minWidth: 0,
  maxWidth: INLINE_WIDTH,
  flexBasis: 'auto',
  flexShrink: 1,
  animation: `${expandBar(INLINE_WIDTH)} 400ms cubic-bezier(0.4, 0, 0.2, 1)`,
  cursor: 'default',
  '&:hover:not(:disabled)': {
    backgroundColor: theme.colors.white,
  },
  [`@media (max-width: ${breakpoints.medium.max}px)`]: {
    width: INLINE_WIDTH_MEDIUM,
    minWidth: 0,
    maxWidth: INLINE_WIDTH_MEDIUM,
    flexBasis: 'auto',
    animation: `${expandBar(INLINE_WIDTH_MEDIUM)} 400ms cubic-bezier(0.4, 0, 0.2, 1)`,
  },
  [`@media (max-width: ${breakpoints.small.max}px)`]: {
    width: INLINE_WIDTH_SMALL,
    minWidth: 0,
    maxWidth: INLINE_WIDTH_SMALL,
    flexBasis: 'auto',
    animation: `${expandBar(INLINE_WIDTH_SMALL)} 400ms cubic-bezier(0.4, 0, 0.2, 1)`,
  },
});

const skeletonStyles = css({
  borderRadius: theme.borderRadius.full,
  backgroundColor: theme.colors.neutral20,
});

const iconContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  gap: theme.spacing(0.25),
});

// ── Popover dialog (transparent wrapper) ───────────────────────────
const popoverDialogStyles = css({
  padding: 0,
  minWidth: POPOVER_WIDTH,
  width: POPOVER_WIDTH,
  maxWidth: POPOVER_WIDTH,
  minHeight: POPOVER_HEIGHT,
  height: POPOVER_HEIGHT,
  borderRadius: POPOVER_HEIGHT / 2,
  border: 'none',
  backgroundColor: 'transparent',
  boxShadow: theme.shadows.light,
  overflow: 'visible',
  position: 'relative',
});

// ── Button style resolver ──────────────────────────────────────────
type ButtonStyleArgs = {
  isCompact?: boolean;
  noBorder?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  popoverMode: boolean;
};

function resolveButtonStyles({ isCompact, noBorder, disabled, isLoading, popoverMode }: ButtonStyleArgs) {
  return [
    // Always-on base: sizing, cursor, flex centering, transition
    baseButtonStyles,
    // Compact + bordered → pill shape with border (in small/mini-chat mode)
    // Otherwise → plain hover highlight (inline mode)
    isCompact && !noBorder ? compactBaseStyles : hoverStyles,
    // Compact + borderless → collapse the pill into the plain icon button
    isCompact && noBorder && css({ padding: theme.spacing(0), animation: `${pillCollapse} 300ms ease forwards` }),
    // Disabled → muted, non-interactive cursor
    disabled && disabledStyles,
    // Inline loading → expand-bar animation with skeleton, no hover background
    isLoading && !popoverMode && inlineLoadingStyles,
  ];
}

// ── Component ──────────────────────────────────────────────────────
export const SmartDraftButton = ({
  onClick,
  onInsert,
  disabled,
  isLoading,
  noBorder,
  mobilePopoverProps,
  usePopoverMode,
  compact,
  onHoverPreview,
  onLeavePreview,
  size = 32,
}: SmartDraftButtonProps) => {
  const { t } = useTranslation('thread-sending-area');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top', hoverDelay: 1000 } as any);
  const isSmallScreen = useMatchMedia({ maxWidth: MOBILE_POPOVER_BREAKPOINT });
  const popoverMode = usePopoverMode ?? isSmallScreen;
  const isCompact = popoverMode || compact;
  const hasPopoverContent = Boolean(mobilePopoverProps);
  const prevHasPopoverContent = useRef(hasPopoverContent);
  const [inserted, setInserted] = useState(false);

  const { getTriggerProps, getDialogProps, close, isOpen } = usePopoverDialog<HTMLButtonElement>({
    placement: 'top',
    interactionOptions: {
      click: { enabled: false },
      hover: { enabled: popoverMode && hasPopoverContent },
      dismiss: { outsidePressEvent: 'pointerdown' },
    },
  });

  // Close popover and reset inserted state when content disappears (redo triggers loading)
  useEffect(() => {
    if (prevHasPopoverContent.current && !hasPopoverContent) {
      close();
      setInserted(false);
    }
    prevHasPopoverContent.current = hasPopoverContent;
  }, [hasPopoverContent, close]);

  // Sync preview text with popover open/close — skip preview when text already inserted
  useEffect(() => {
    if (!popoverMode) return;
    if (isOpen && !inserted) {
      onHoverPreview?.();
    } else {
      onLeavePreview?.();
    }
  }, [popoverMode, isOpen, inserted, onHoverPreview, onLeavePreview]);

  const handleInsert = useCallback(() => {
    if (onInsert) {
      onInsert();
    } else {
      mobilePopoverProps?.onTextClick();
    }
    setInserted(true);
    close();
  }, [onInsert, mobilePopoverProps, close]);

  const handleClick = useCallback(() => {
    if (disabled || isLoading) return;
    if (popoverMode && hasPopoverContent) {
      // In popover/mobile mode, clicking the button should only show the popover (hover already does) — no insert here.
      return;
    }
    onClick();
  }, [disabled, isLoading, popoverMode, hasPopoverContent, onClick]);

  const { ref: _tooltipRef, ...tooltipTriggerProps } = triggerProps;

  // Inline-only hover preview (popover mode uses the isOpen effect above)
  const handleMouseEnter = useCallback(() => {
    if (!popoverMode && onHoverPreview && mobilePopoverProps?.text) {
      onHoverPreview();
    }
  }, [popoverMode, mobilePopoverProps, onHoverPreview]);

  const handleMouseLeave = useCallback(() => {
    if (!popoverMode) {
      onLeavePreview?.();
    }
  }, [popoverMode, onLeavePreview]);

  // Wrap mobile popover actions: close calls through to parent onClose, redo resets inserted state
  const mobileResultBarProps = useMemo(() => {
    if (!mobilePopoverProps) return undefined;
    return {
      ...mobilePopoverProps,
      onTextClick: () => {},
      onRedo: () => {
        setInserted(false);
        mobilePopoverProps.onRedo();
      },
      onClose: () => {
        close();
        mobilePopoverProps.onClose();
      },
    };
  }, [mobilePopoverProps, handleInsert, close]);

  const buttonTriggerProps = popoverMode
    ? getTriggerProps({
        ...tooltipTriggerProps,
        onClick: handleClick,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      })
    : { ...triggerProps, onClick: handleClick, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave };

  if (popoverMode && isLoading) {
    return <SmartDraftMobileLoader />;
  }

  return (
    <>
      <NakedButton
        {...buttonTriggerProps}
        disabled={disabled || isLoading}
        css={resolveButtonStyles({ isCompact, noBorder, disabled, isLoading, popoverMode })}
      >
        <div css={iconContainerStyles}>
          <Icon
            name='smart-draft'
            size={size}
            data-trackingid={`${InboxPrefixes.Thread}-smart-draft-generate-btn`}
            css={{ color: isLoading || disabled ? theme.colors.neutral20 : theme.colors.neutral90, flexShrink: 0 }}
          />
          {isLoading && !popoverMode && (
            <SkeletonLoader animation='shimmer' height={8} css={[{ flex: 1, minWidth: 0 }, skeletonStyles]} />
          )}
        </div>
      </NakedButton>
      {!isLoading && <Tooltip {...tooltipProps}>{t('Create smart reply')}</Tooltip>}
      {popoverMode && hasPopoverContent && mobileResultBarProps && (
        <PopoverDialog {...getDialogProps()} css={popoverDialogStyles}>
          <SmartDraftResultBar {...mobileResultBarProps} variant='mobile' />
        </PopoverDialog>
      )}
    </>
  );
};
